import React, { useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

/**
 * @see https://developer.auth0.com/resources/guides/spa/react/basic-authentication
 * @param component
 * @constructor
 */
export function AuthenticationGuard({
  component,
}: {
  component: React.ComponentType;
}) {
  // Check if we should skip authentication for development
  const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

  // If skipping auth, return the component directly
  if (skipAuth) {
    const Component = component;
    return <Component />;
  }

  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();

  useEffect(() => {
    // If not loading, not authenticated, and we have an error (likely expired session)
    // or if we're not authenticated and not loading, trigger login
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => <LoadingIndicator />,
    returnTo: window.location.pathname,
  });

  // Show loading while Auth0 is checking authentication status
  if (isLoading) {
    return <LoadingIndicator />;
  }

  // If there's an authentication error, trigger login redirect
  if (error) {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
    return <LoadingIndicator />;
  }

  return <Component />;
}
